import { clearAuth, getAuthState, getRefreshToken, saveAuth } from './auth';
const LOCAL_API_BASE_URL = 'http://localhost:8083';
const PROD_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.aevdir.com';

// env 约定：
// - 默认：local（本地开发，走 localhost:8083）
// - Docker / 正式环境：NODE_ENV === 'production' 视为 prod，走线上 URL
// - 也可以通过 REACT_APP_API_ENV=local|prod 显式指定
const API_ENV =
  process.env.REACT_APP_API_ENV ||
  (process.env.NODE_ENV === 'production' ? 'prod' : 'local');

const API_BASE_URL = API_ENV === 'local' ? LOCAL_API_BASE_URL : PROD_API_BASE_URL;
export { API_BASE_URL };
let refreshPromise = null;

function isAuthPath(path) {
  return path === '/api/auth/login' || path === '/api/auth/register' || path === '/api/auth/refresh';
}

function buildHeaders(options, withAuth) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (!withAuth) {
    return headers;
  }
  const auth = getAuthState();
  if (auth?.accessToken) {
    headers.Authorization = `${auth.tokenType || 'Bearer'} ${auth.accessToken}`;
  }
  return headers;
}

async function requestRaw(path, options = {}, withAuth = true) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: buildHeaders(options, withAuth),
  });
  const payload = await response.json().catch(() => ({}));
  return { response, payload };
}

function canRefresh(path, retried) {
  return !retried && !isAuthPath(path) && Boolean(getRefreshToken());
}

async function runRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('login expired');
  }
  const { response, payload } = await requestRaw(
    '/api/auth/refresh',
    {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    },
    false,
  );
  if (!response.ok || payload.code !== 200 || !payload.data) {
    throw new Error(payload.msg || 'refresh failed');
  }
  const current = getAuthState();
  saveAuth({
    ...current,
    ...payload.data,
    user: payload.data.user || current?.user || null,
  });
  return payload.data;
}

export async function request(path, options = {}) {
  const retried = Boolean(options.__retried);
  const requestOptions = { ...options };
  delete requestOptions.__retried;

  const { response, payload } = await requestRaw(path, requestOptions, path !== '/api/auth/refresh');
  if (response.ok && payload.code === 200) {
    return payload.data;
  }

  if (response.status === 401 && canRefresh(path, retried)) {
    try {
      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return request(path, { ...requestOptions, __retried: true });
    } catch (_) {
      clearAuth();
      throw new Error('login expired');
    }
  }

  throw new Error(payload.msg || 'request failed');
}

export async function uploadRequest(path, formData, retried = false) {
  const auth = getAuthState();
  const headers = {};
  if (auth?.accessToken) {
    headers.Authorization = `${auth.tokenType || 'Bearer'} ${auth.accessToken}`;
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const payload = await response.json().catch(() => ({}));
  if (response.ok && payload.code === 200) {
    return payload.data;
  }
  if (response.status === 401 && !retried && getRefreshToken()) {
    try {
      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return uploadRequest(path, formData, true);
    } catch (_) {
      clearAuth();
      throw new Error('login expired');
    }
  }
  throw new Error(payload.msg || 'request failed');
}
